'use client'
import * as React from 'react'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  initReplicache,
  useReplicacheParamsStore,
} from '../replicache/frontend/init'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import {
  branchBaseOps,
  isPlayground,
  UserPreferences,
} from '@basehub/data/replicache/index'
import { getRepoSpaceName } from '@basehub/data/replicache/mutators/team-space/helpers'
import {
  TeamReplicacheQueryData,
  useTeamReplicache,
} from './use-team-replicache'
import { useDashboardSlugs } from '../hooks/use-dashboard-slugs'
import { dropDatabase, type Replicache } from 'replicache'
import type { Branch, Repository, Team } from '@basehub/data/replicache/index'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import {
  parseTab,
  useTabsStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { match } from 'ts-pattern'
import { clientPingIntervalMs } from '~/lib/time/presence'
import { useIsOnline } from '~/hooks/use-network-status'
import { repositoryMutators } from '@basehub/data/replicache/mutators/team-space/repository/mutators'
import { useUserStore } from '~/app/_store/user-store'
import {
  getSelectedBranchOrCommitQueryKey,
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useEditorStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/editor-store'
import { useActivePublicTeam } from './team/use-active-public-team'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { checkIndexedDBStorageEstimate } from '~/lib/storage-util'
import { useIsGuest, useIsGuestInHisBranch } from './use-user-role'
import { transactionClientContext } from '@basehub/data/replicache/tx/iso'
import { useUserData } from './use-user-data'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { RepoReplicacheSyncStateProvider } from '~/app/(dash)/[team-slug]/header/repo-replicache-sync-state-provider'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'
import { isBefore } from 'date-fns'
import { getPlaygroundExpirationDate } from '@basehub/data/replicache/playground/utils'
import { JS_MAX_TIMEOUT_MS } from '@basehub/shared-constants'

type RepoReplicacheContext = {
  data: QueryData | undefined
  isLoading: boolean
  isError: boolean
}

const RepoReplicacheContext = React.createContext<
  RepoReplicacheContext | undefined
>(undefined)

/**
 * Initializes replicache for repository, and, as we've already fetched the team replicache,
 * we also return it here.
 */
export const RepoReplicacheProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const teamReplicacheQuery = useTeamReplicache()
  const { data: activePublicTeam } = useActivePublicTeam()
  const repoSlug = useDashboardSlugs((s) => s.repoSlug)
  const branchOrCommit = useDashboardSlugs((s) => s.branchOrCommit)
  const playgroundId = usePlaygroundCookie()
  const { data: optimisticUser } = useUserData(null)
  const queryClient = useQueryClient()
  const team = teamReplicacheQuery.data?.team ?? activePublicTeam?.team

  const queryKey = getRepoReplicacheQueryKey({
    teamSlug: team?.slug ?? null,
    userId: optimisticUser?.id ?? null,
    repoSlug,
  })

  const { data: repoResult } = useReplicacheQueryV2({
    key: ['_repo', team?.id, repoSlug],
    fetcher: async (tx) => {
      if (!team || !repoSlug) {
        throw new Error("shouldn't be enabled")
      }

      if (activePublicTeam?.repo) return activePublicTeam.repo

      const repoQueryResult = await teamSpaceMutators.getRepoBySlug(
        tx,
        repoSlug
      )

      return repoQueryResult ?? null
    },
    replicache: teamReplicacheQuery.data?.replicache,
    enabled: !!team && !!repoSlug,
  })

  const repo = repoResult ?? activePublicTeam?.repo

  const query = useQuery<QueryData>(
    queryKey,
    async () => {
      if (!repo || !team || !optimisticUser) {
        throw new Error("shouldn't be enabled")
      }

      const result = await fetcher({
        team,
        repo,
        teamQueryData: teamReplicacheQuery.data,
        userId: optimisticUser.id,
        branchId: branchOrCommit ?? '',
        isGuest: false,
        isGuestInHisBranch: false,
        playgroundId: playgroundId ?? undefined,
      })

      return result
    },
    {
      enabled:
        Boolean(repoSlug) &&
        !!team &&
        !!repo &&
        Boolean(optimisticUser) &&
        repoSlug !== 'integrations',
      staleTime: Infinity,
      retry: 3,
    }
  )

  type Data = typeof query.data

  const teamReplicache = teamReplicacheQuery.data?.replicache

  React.useEffect(() => {
    if (!teamReplicache || !repo?.id) return

    const unsub = teamReplicache.subscribe(
      (tx) => repositoryMutators.getRepository(tx, repo.id),
      {
        onData: (repo) => {
          queryClient.setQueryData<Data>(queryKey, (p) => {
            if (!p) return p
            if (!repo) return p
            return {
              ...p,
              repo: {
                ...repo,
                status: repo.status ?? null,
                isPublic: repo.isPublic ?? null,
              },
            }
          })
        },
      }
    )

    return () => {
      unsub()
    }
  }, [queryClient, queryKey, teamReplicache, repo?.id])

  // refresh replicache w a query param
  React.useEffect(() => {
    if (!query.data?.replicache) return
    const searchParams = new URLSearchParams(window.location.search)
    if (typeof searchParams.get('refresh-replicache') === 'string') {
      dropDatabase(query.data.replicache.idbName)
      searchParams.delete('refresh-replicache')
      let search = searchParams.toString()
      if (search) search = `?${search}`
      window.location.href =
        window.location.pathname + search + window.location.hash
    }
  }, [query.data?.replicache])

  // keep in sync with the branchOrCommit param
  React.useEffect(() => {
    useReplicacheParamsStore.setState((p) => {
      return {
        repo: {
          ...p.repo,
          branchOrCommit: branchOrCommit ?? undefined,
          playgroundId: playgroundId ?? undefined,
        },
      }
    })
    query.data?.replicache.pull()
  }, [branchOrCommit, playgroundId, query.data?.replicache])

  /**
   * clean up playgrounds that have expired (the server won't return them cause they'll be deleted)
   */
  React.useEffect(() => {
    if (!playgroundId) return
    if (!query.data?.replicache) return

    let timeout = 0

    const unsub = query.data.replicache.subscribe(
      (tx) => branchBaseOps.get(tx, playgroundId),
      {
        onData: async (playgroundBranch) => {
          if (
            !playgroundBranch ||
            playgroundBranch.playgroundId !== playgroundId ||
            !isPlayground(playgroundBranch)
          ) {
            return
          }
          const pgExpirationDate = getPlaygroundExpirationDate(playgroundBranch)
          const pgExpirationDateIso = pgExpirationDate.toISOString()
          const expired = isBefore(pgExpirationDate, new Date())

          const expireAction = async () => {
            window.clearTimeout(timeout)
            const ssKey = '_refreshing_expired_playground_' + playgroundId
            const alreadyRefreshed =
              sessionStorage.getItem(ssKey) === pgExpirationDateIso
            if (alreadyRefreshed) {
              console.error({
                playgroundBranch,
                playgroundId,
                expDate: getPlaygroundExpirationDate(playgroundBranch),
              })
              throw new Error(
                'Attempting to re-refresh expired playground. Throwing to prevent an infinite loop.'
              )
            }
            await dropDatabase(query.data.replicache.idbName)
            sessionStorage.setItem(ssKey, pgExpirationDateIso)
            window.location.reload()
          }

          if (expired) {
            await expireAction()
          } else {
            // set a timeout for when it actually expires
            const timeoutMs = pgExpirationDate.getTime() - Date.now() + 3_000 // give the inngest job couple seconds of grace time
            if (timeoutMs > JS_MAX_TIMEOUT_MS) return
            timeout = window.setTimeout(async () => {
              await expireAction()
            }, timeoutMs)
          }
        },
      }
    )
    return () => {
      unsub()
      window.clearTimeout(timeout)
    }
  }, [playgroundId, query.data?.replicache])

  React.useEffect(() => {
    checkIndexedDBStorageEstimate().then((estimate) => {
      if (estimate?.usage !== undefined && estimate.quota !== undefined) {
        // eslint-disable-next-line no-console
        console.log(
          `IDB: using ${estimate.usage} out of ${
            estimate.quota
          } bytes. Approximately ${Math.round(
            (estimate.usage / estimate.quota) * 100
          )}% used`
        )
      }
    })
  }, [])

  return (
    <RepoReplicacheContext.Provider
      value={React.useMemo(
        () => ({
          data: query.data,
          isLoading: query.isInitialLoading,
          isError: query.isError,
        }),
        [query.data, query.isError, query.isInitialLoading]
      )}
    >
      <RepoReplicacheSyncStateProvider>
        {children}
        <TxContextSetter />
      </RepoReplicacheSyncStateProvider>
    </RepoReplicacheContext.Provider>
  )
}

const TxContextSetter = () => {
  const { data } = useRepoReplicache()
  const isGuest = useIsGuest()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const branchOrCommit = useSelectedBranchOrCommitQuery().data
  const playgroundId = usePlaygroundCookie()

  React.useEffect(() => {
    if (!data?.clientId) return
    transactionClientContext.setState((curr) => {
      const ctx = curr.get(data.clientId)
      if (!ctx) return curr
      ctx.isGuest = !!isGuest || !!isGuestInHisBranch
      ctx.isGuestInHisBranch = !!isGuestInHisBranch
      ctx.branchOrCommit = branchOrCommit ?? null
      ctx.playgroundId = playgroundId ?? undefined
      return curr
    })
  }, [
    data?.clientId,
    isGuest,
    isGuestInHisBranch,
    branchOrCommit,
    playgroundId,
  ])

  return null
}

export const useRepoReplicache = () => {
  const context = React.useContext(RepoReplicacheContext)
  if (!context) {
    throw new Error(
      'useRepoReplicache must be used within a RepoReplicacheProvider'
    )
  }
  return context
}

// types
type QueryData = {
  replicache: Replicache<typeof repoSpaceMutators>
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  spaceId: string
  repoId: string
  repoSlug: string
  clientId: string
  teamQueryData: ReturnType<typeof useTeamReplicache>['data']
  initialUserPreferences: UserPreferences
  initializedAt: string
}

// utils
export function getRepoReplicacheQueryKey({
  teamSlug,
  userId,
  repoSlug,
}: {
  teamSlug: string | null
  userId: string | null
  repoSlug: string | null
}) {
  return ['repo-replicache', teamSlug, userId, repoSlug] as const
}

// pre-populate the cache
export async function prePopulateRepoReplicache({
  repo,
  firstBranch,
  teamReplicacheQuery,
  queryClient,
  userId,
  isGuest,
  isGuestInHisBranch,
  playgroundId,
}: {
  repo: Repository
  firstBranch: Branch
  teamReplicacheQuery: ReturnType<typeof useTeamReplicache>
  queryClient: QueryClient
  userId: string
  isGuest: boolean
  isGuestInHisBranch: boolean
  playgroundId: string | undefined
}) {
  if (!teamReplicacheQuery.data) {
    throw new Error('not ready')
  }
  const teamSlug = teamReplicacheQuery.data.teamSlug

  const queryKey = getRepoReplicacheQueryKey({
    teamSlug,
    userId,
    repoSlug: repo.slug,
  })

  const prepopulatedData = await fetcher({
    repo,
    team: teamReplicacheQuery.data.team,
    teamQueryData: teamReplicacheQuery.data,
    userId,
    branchId: firstBranch.id,
    isGuest,
    isGuestInHisBranch,
    playgroundId,
  })

  queryClient.setQueryData<QueryData>(queryKey, prepopulatedData)
  queryClient.setQueryData(
    getSelectedBranchOrCommitQueryKey({
      spaceId: prepopulatedData.spaceId,
      branchOrCommit: firstBranch.name,
      playgroundId,
    }),
    { result: firstBranch, isFallback: false }
  )
  queryClient.setQueryData(
    getSelectedBranchOrCommitQueryKey({
      spaceId: prepopulatedData.spaceId,
      branchOrCommit: null,
      playgroundId,
    }),
    { result: firstBranch, isFallback: false }
  )
}

// fetcher
async function fetcher({
  team,
  repo,
  userId,
  branchId,
  teamQueryData,
  isGuest,
  isGuestInHisBranch,
  playgroundId,
}: {
  team: Team | PublicTeam | null
  repo: Repository | PublicRepo | null
  teamQueryData: TeamReplicacheQueryData | undefined | null
  userId: string
  branchId: string
  isGuest: boolean
  isGuestInHisBranch: boolean
  playgroundId: string | undefined
}): Promise<QueryData> {
  if (!repo?.slug || !team) {
    throw new Error("shouldn't be enabled")
  }

  const repoSpaceId = getRepoSpaceName(team.id, repo.id)

  // init replicache
  const { replicache } = await initReplicache({
    spaceID: repoSpaceId,
    mutators: repoSpaceMutators,
    type: 'repo',
    userId,
    clientOnly: {
      repo,
      team,
      isGuest,
      isGuestInHisBranch,
      branchOrCommit: null,
      playgroundId,
    },
  })

  const clientId = replicache.clientID

  // init user-preferences, if they don't yet exist
  const [initialUserPreferences] = await Promise.all([
    replicache.mutate.initUserPreferences(userId),
    // and multiplayer client
    initPresenceClient({
      clientId,
      replicache,
      spaceId: repoSpaceId,
      userId,
      branchId: playgroundId ? playgroundId : branchId,
    }),
  ])

  return {
    spaceId: repoSpaceId,
    replicache,
    team,
    repo,
    repoId: repo.id,
    repoSlug: repo.slug,
    teamQueryData,
    initialUserPreferences,
    clientId,
    initializedAt: new Date().toISOString(),
  }
}

async function initPresenceClient({
  branchId,
  spaceId,
  clientId,
  userId,
  replicache,
}: {
  branchId: string
  spaceId: string
  clientId: string
  userId: string
  replicache: Replicache<typeof repoSpaceMutators>
}) {
  const pickColor = () => {
    const COLORS = [
      'var(--presence-red)',
      'var(--presence-yellow)',
      'var(--presence-green)',
      'var(--presence-cyan)',
      'var(--presence-blue)',
      'var(--presence-purple)',
      'var(--presence-pink)',
    ]
    return COLORS[Math.floor(Math.random() * COLORS.length)] as string
  }

  // we don't want to create more than one client per session
  // i mean, when i refresh the webpage, this code will run again (will reinit a new replicache client)
  // but we don't want to show many clients in the multiplayer list

  const sessionStorageKey = `bshb-presence-client-id-${spaceId}`

  const oldClientButInTheSameSession =
    window.sessionStorage.getItem(sessionStorageKey)

  // so we store the new client id in session storage
  window.sessionStorage.setItem(sessionStorageKey, clientId)

  await replicache.mutate.initClient({
    branchId,
    id: userId,
    userId,
    color: pickColor(),
    now: Date.now(),
    killDuplicateClientId: oldClientButInTheSameSession, // we pass the old client id to be deleted at the same time the new one is initialized
  })
}

export const DISABLE_CLIENT_UPDATES = false

export const KeepClientAlivePinger = () => {
  const { data } = useRepoReplicache()
  const isOnline = useIsOnline()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const { branch } = useSelectedBranch()
  const playgroundId = usePlaygroundCookie()

  React.useEffect(() => {
    if (DISABLE_CLIENT_UPDATES) return
    if (!data?.replicache || !optimisticUser?.id || !isOnline) {
      return
    }

    const interval = setInterval(() => {
      data.replicache.mutate.keepClientAlive({
        id: optimisticUser.id,
        branchId: branch?.id ?? '',
        now: Date.now(),
      })
    }, clientPingIntervalMs)

    return () => {
      clearInterval(interval)
    }
  }, [
    data?.replicache,
    optimisticUser?.id,
    isOnline,
    branch?.id,
    branch?.isDefault,
    playgroundId,
  ])

  return null
}

export const ClientSelectionUpdater = () => {
  const { data } = useRepoReplicache()
  const activeTab = useTabsStore((s) => s.activeTab)

  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const isOnline = useIsOnline()
  const { branch } = useSelectedBranch()
  const playgroundId = usePlaygroundCookie()

  const selectedBlockIdWithFallback = useEditorStore((s) => {
    return (
      s.selectedBlockId ??
      (activeTab
        ? match(parseTab(activeTab))
            .with({ type: 'block' }, (r) => r.blockId)
            .with({ type: 'block:diff' }, (r) => r.blockId)
            .otherwise(() => null)
        : null)
    )
  })
  const selectedBlockAwareness = useEditorStore((s) => s.selectedBlockAwareness)

  React.useEffect(() => {
    if (DISABLE_CLIENT_UPDATES) return
    if (!data?.replicache?.mutate || !isOnline || !optimisticUser?.id) {
      return
    }

    data.replicache.mutate.updateClientFocus({
      id: optimisticUser.id,
      now: Date.now(),
      branchId: branch?.id ?? '',
      selection: selectedBlockIdWithFallback
        ? {
            blockId: selectedBlockIdWithFallback,
            awareness: selectedBlockAwareness,
            activeTabKey: activeTab?.key,
          }
        : null,
    })
  }, [
    data?.replicache?.mutate,
    optimisticUser?.id,
    data?.clientId,
    selectedBlockIdWithFallback,
    isOnline,
    selectedBlockAwareness,
    activeTab?.key,
    branch?.id,
    branch?.isDefault,
    playgroundId,
  ])

  return null
}
